import type { TourConfig } from '../common/Tour/tour.types';
import {
  findTourTarget,
  pause,
  clickTarget,
  isMuiCheckboxChecked,
  closeMuiDialog,
  closeMuiPopover,
} from '../common/Tour/tour.utils';

/**
 * Ensure the discovery filters popover is open.
 * If it's already open (the reviewed-only checkbox is visible), do nothing.
 */
const ensureDiscoveryPopoverOpen = async (): Promise<void> => {
  if (findTourTarget('[data-cy="checkbox-mat-reviewed-only"]')) return;
  // Ensure "Discovered" is checked so the filter button is enabled
  if (!isMuiCheckboxChecked('[data-cy="checkbox-mat-discovered"]')) {
    await clickTarget('[data-cy="checkbox-mat-discovered"]');
    await pause(300);
  }
  await clickTarget('[data-cy="btn-discovery-filters"]');
};

/**
 * Close a MUI Drawer by clicking its close IconButton (the one containing an SVG close icon).
 * Falls back to clicking the MUI backdrop.
 */
const closeDrawer = async (): Promise<void> => {
  // The drawer's close button is an IconButton without aria-label,
  // so find it by looking for the CloseIcon SVG inside the drawer header.
  const drawer = document.querySelector('[data-cy="mat-row-detail-drawer"]');
  if (drawer) {
    const paper = drawer.querySelector<HTMLElement>('.MuiPaper-root');
    if (paper) {
      // The close button is an IconButton containing a CloseIcon SVG (path with "M 19 6.41...")
      const buttons = paper.querySelectorAll<HTMLElement>('button.MuiIconButton-root');
      for (const btn of buttons) {
        if (btn.querySelector('[data-testid="CloseIcon"]')) {
          btn.click();
          await pause(400);
          return;
        }
      }
    }
  }
  // Fallback: click the MUI backdrop
  const backdrop = document.querySelector<HTMLElement>('.MuiDrawer-root .MuiBackdrop-root');
  if (backdrop) {
    backdrop.click();
    await pause(400);
  }
};

export const matPageTourConfig: TourConfig = {
  id: 'mat-page',
  steps: [
    // Step 1: MAT Entries / Discovered count
    {
      target: '[data-cy="mat-total-count"]',
      title: 'Entry Counts',
      content:
        'Shows the number of MAT entries and Discovered entries currently visible in the grid.',
      placement: 'bottom',
    },

    // Step 2: Discovered checkbox
    {
      target: '[data-cy="checkbox-mat-discovered"]',
      title: 'Discovered Data',
      content:
        'Toggle this checkbox to include or exclude discovered MAT entries in the grid. Discovered entries are automatically identified from your documents.',
      placement: 'bottom',
    },

    // Step 3: Discovery filters button (click to open popover)
    {
      target: '[data-cy="btn-discovery-filters"]',
      title: 'Discovery Filters',
      content:
        'Click this filter icon to access additional filters for discovered data, including reviewed-only and vendor status filters.',
      placement: 'left',
      zIndexBoost: true,
      beforeAction: ensureDiscoveryPopoverOpen,
      waitFor: '[data-cy="checkbox-mat-reviewed-only"]',
    },

    // Step 4: Reviewed Only checkbox
    {
      target: '[data-cy="checkbox-mat-reviewed-only"]',
      title: 'Reviewed Only',
      content:
        'Enable this to filter discovered data to only show entries that have been reviewed.',
      placement: 'bottom',
      zIndexBoost: true,
      beforeAction: ensureDiscoveryPopoverOpen,
      waitFor: '[data-cy="checkbox-mat-reviewed-only"]',
    },

    // Step 5: Vendor Status filter
    {
      target: '[data-cy="btn-vendor-exists-filter"]',
      title: 'Vendor Status',
      content:
        'Filter discovered entries by vendor status. Choose "Has Vendor" to see entries matched to existing vendors, or "No Vendor" for unmatched ones.',
      placement: 'bottom',
      zIndexBoost: true,
      beforeAction: ensureDiscoveryPopoverOpen,
      waitFor: '[data-cy="btn-vendor-exists-filter"]',
      onLeave: closeMuiPopover,
    },

    // Step 6: MAT Data checkbox
    {
      target: '[data-cy="checkbox-mat-data"]',
      title: 'MAT Data',
      content: 'Toggle this to include or exclude MAT data entries.',
      placement: 'bottom',
    },

    // Step 7: Export button
    {
      target: '[data-cy="btn-export-mat"]',
      title: 'Export',
      content:
        'Export all of the MAT data for this data pool. This will not include any filters or discovered entries.',
      placement: 'bottom',
    },

    // Step 8: Template button
    {
      target: '[data-cy="btn-template-mat"]',
      title: 'Template',
      content: 'Download a MAT template for this data pool.',
      placement: 'bottom',
    },

    // Step 9: Import button
    {
      target: '[data-cy="btn-import-mat"]',
      title: 'Import',
      content: 'Import data to update MAT entries.',
      placement: 'bottom',
    },

    // Step 10: Data grid
    {
      target: '[data-cy="mat-grid"]',
      title: 'Data Grid',
      content:
        'This is the main data grid showing your MAT entries. You can sort columns, resize them, and scroll through your data. The grid loads data on demand as you scroll.\n\n🟢 Green rows — Discovered entries (always at the top)\n🟡 Yellow rows — Existing MAT entries that also have discovered rows\n⚪ White rows — Existing MAT entries with no discovered rows',
      placement: 'top',
      spotlightPadding: 4,
      centerTooltip: true,
      fallbackWhen: () => !document.querySelector('[data-cy="mat-grid"] .ag-row'),
      fallbackImage: '/images/tour/mat/ag-grid-with-rows.webp',
    },

    // Step 11: AG Grid floating filters
    {
      target: '.ag-floating-filter:has(input)',
      title: 'Column Filters',
      content:
        'Filter the combined set of MAT entries by typing in these filter inputs above each column.',
      placement: 'bottom',
      fallbackImage: '/images/tour/mat/column-filters.webp',
    },

    // Step 12: Expand icon (just highlight, don't click yet)
    // When no rows exist, shows a screenshot instead of skipping
    {
      target: '[data-cy="expand-icon"]',
      title: 'Expand Row',
      content:
        'Click this arrow icon on any row to open the detail drawer. The drawer shows discovery data and allows you to review or update field values.',
      placement: 'right',
      fallbackImage: '/images/tour/mat/expand-row.webp',
    },

    // Step 13: Row Detail Drawer (click expand, wait for drawer)
    {
      target: '[data-cy="mat-row-detail-drawer"] .MuiPaper-root',
      title: 'Row Detail Drawer',
      content:
        'This contains the build row (at the top), the existing MAT entry (if exists) and then all discovered rows. You can navigate through each column clicking the copy icon from any row to copy the value from the row into the build row. Do this for all columns that you need to set, then click the CREATE or UPDATE button at the bottom.',
      placement: 'top',
      spotlightPadding: 0,
      zIndexBoost: true,
      beforeAction: async () => {
        await clickTarget('[data-cy="expand-icon"]');
        await pause(500);
      },
      waitFor: '[data-cy="mat-row-detail-drawer"] .MuiPaper-root',
      onLeave: async () => {
        await closeDrawer();
      },
      fallbackImage: '/images/tour/mat/row-detail-drawer.webp',
    },

    // Step 14: Vendor ticket icon
    // When no discovered rows with vendor names exist, shows a screenshot instead
    {
      target: '[data-cy="vendor-ticket-icon"]',
      title: 'Vendor Tickets',
      content:
        'This icon appears on discovered rows that have a normalized vendor name. Click it to view and manage vendor change tickets for that vendor.',
      placement: 'right',
      fallbackImage: '/images/tour/mat/vendor-change-tickets.webp',
    },

    // Step 15: Vendor ticket dialog (click icon, wait for dialog)
    {
      target: '#dialog-vendor-change-tickets',
      title: 'Vendor Ticket Dialog',
      content:
        'This dialog shows pending and rejected vendor change tickets. You can navigate to the Vendors page for full ticket management.',
      placement: 'top',
      zIndexBoost: true,
      beforeAction: async () => {
        await clickTarget('[data-cy="vendor-ticket-icon"]');
      },
      waitFor: '#dialog-vendor-change-tickets',
      onLeave: async () => {
        await closeMuiDialog('dialog-vendor-change-tickets');
      },
      fallbackImage: '/images/tour/mat/vendor-change-tickets.webp',
    },
  ],
};
